const EMPTY_ARRAY: never[] = [];
// Password validation utilities
export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  minSpecialChars: 1,
  prohibitCommonPasswords: true,
  prohibitPersonalInfo: true,
};

// Common weak passwords to reject
const COMMON_WEAK_PASSWORDS = [
  'password',
  'password123',
  '123456',
  '123456789',
  'qwerty',
  'abc123',
  'password1',
  'client_admin',
  'letmein',
  'welcome',
  'monkey',
  '1234567890',
  'dragon',
  'master',
  'login',
  'pass',
  'football',
  'baseball',
  'superman',
];

interface UserInfo {
  email?: string;
  displayName?: string;
  clientName?: string;
}

export const validatePassword = (password: string, userInfo: UserInfo = {}) => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Basic length requirements
  if (!password || password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`);
  }

  if (password && password.length > PASSWORD_REQUIREMENTS.maxLength) {
    errors.push(`Password must be no more than ${PASSWORD_REQUIREMENTS.maxLength} characters long`);
  }

  if (!password) {
    return { isValid: false, errors, warnings, strength: 0 };
  }

  // Character type requirements
  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (PASSWORD_REQUIREMENTS.requireNumbers && !/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (PASSWORD_REQUIREMENTS.requireSpecialChars) {
    const specialCharCount = (
      password.match(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/g) || EMPTY_ARRAY
    ).length;
    if (specialCharCount < PASSWORD_REQUIREMENTS.minSpecialChars) {
      errors.push(
        `Password must contain at least ${PASSWORD_REQUIREMENTS.minSpecialChars} special character(s) (!@#$%^&*()_+-=[]{}|;':"\\,./<>?~)`
      );
    }
  }

  // Common password checks
  if (PASSWORD_REQUIREMENTS.prohibitCommonPasswords) {
    const lowerPassword = password.toLowerCase();
    if (COMMON_WEAK_PASSWORDS.includes(lowerPassword)) {
      errors.push('Password is too common. Please choose a more unique password');
    }
  }

  // Personal information checks
  if (PASSWORD_REQUIREMENTS.prohibitPersonalInfo && userInfo) {
    const { email, displayName, clientName } = userInfo;
    const lowerPassword = password.toLowerCase();

    if (email?.includes('@')) {
      const username = email.split('@')[0];
      if (username && lowerPassword.includes(username.toLowerCase())) {
        errors.push('Password should not contain your email username');
      }
    }

    if (displayName) {
      const nameParts = displayName.toLowerCase().split(' ');
      for (const part of nameParts) {
        if (part.length > 2 && lowerPassword.includes(part)) {
          errors.push('Password should not contain your name');
          break;
        }
      }
    }

    if (clientName && clientName.length > 3 && lowerPassword.includes(clientName.toLowerCase())) {
      errors.push('Password should not contain your company name');
    }
  }

  // Additional security warnings (don't prevent password but warn user)
  if (password.length < 12) {
    warnings.push('Consider using a longer password for better security');
  }

  // Check for patterns
  if (/(.)\1{2,}/.test(password)) {
    warnings.push('Avoid repeating the same character multiple times');
  }

  if (/123|abc|qwe|asd|zxc/i.test(password)) {
    warnings.push('Avoid keyboard patterns for better security');
  }

  // Calculate password strength score (0-100)
  const strength = calculatePasswordStrength(password);

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    strength,
  };
};

export const calculatePasswordStrength = (password: string) => {
  if (!password) {
    return 0;
  }

  let score = 0;

  // Length scoring (up to 30 points) - increased from 25
  score += Math.min(password.length * 2.5, 30);

  // Character variety (up to 40 points total)
  if (/[a-z]/.test(password)) {
    score += 8; // increased from 5
  }
  if (/[A-Z]/.test(password)) {
    score += 8; // increased from 5
  }
  if (/\d/.test(password)) {
    score += 8; // increased from 5
  }
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)) {
    score += 16; // increased from 10
  }

  // Count different character types
  const types = [
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /\d/.test(password),
    /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password),
  ].filter(Boolean).length;

  // Multiple character types bonus (increased)
  if (types >= 3) {
    score += 15; // increased from 10
  }
  if (types >= 4) {
    score += 10; // increased from 5
  }

  // Enhanced length bonuses
  if (password.length >= 10) {
    score += 5; // new bonus for 10+ chars
  }
  if (password.length >= 12) {
    score += 8; // increased from 10
  }
  if (password.length >= 14) {
    score += 7; // new bonus for 14+ chars
  }
  if (password.length >= 16) {
    score += 10; // kept same
  }

  // Bonus for high entropy/randomness (detect likely generated passwords)
  const hasRandomPattern = checkRandomPattern(password);
  if (hasRandomPattern) {
    score += 10;
  }

  // Deduct points for weaknesses
  if (COMMON_WEAK_PASSWORDS.includes(password.toLowerCase())) {
    score -= 30;
  }
  if (/(.)\1{2,}/.test(password)) {
    score -= 15; // increased penalty
  }
  if (/123|abc|qwe|asd|zxc/i.test(password)) {
    score -= 15; // increased penalty
  }

  return Math.max(0, Math.min(100, score));
};

// Helper function to detect likely generated/random passwords
const checkRandomPattern = (password: string) => {
  // Check for good mix of character types throughout the password
  const segments = password.match(/.{1,3}/g) || EMPTY_ARRAY;
  let mixedSegments = 0;

  for (const segment of segments) {
    const hasUpper = /[A-Z]/.test(segment);
    const hasLower = /[a-z]/.test(segment);
    const hasNumber = /\d/.test(segment);
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(segment);

    const typeCount = [hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length;
    if (typeCount >= 2) {
      mixedSegments++;
    }
  }

  // If most segments have mixed character types, likely generated
  return mixedSegments >= segments.length * 0.6;
};

export const getPasswordStrengthLabel = (strength: number) => {
  if (strength < 30) {
    return { label: 'Very Weak', color: '#f44336' };
  }
  if (strength < 50) {
    return { label: 'Weak', color: '#ff9800' };
  }
  if (strength < 70) {
    return { label: 'Fair', color: '#ffeb3b' };
  }
  if (strength < 85) {
    return { label: 'Good', color: '#8bc34a' };
  }
  return { label: 'Strong', color: '#4caf50' };
};

export const getPasswordRequirementsText = () => {
  return [
    `At least ${PASSWORD_REQUIREMENTS.minLength} characters long`,
    'At least one uppercase letter (A-Z)',
    'At least one lowercase letter (a-z)',
    'At least one number (0-9)',
    `At least ${PASSWORD_REQUIREMENTS.minSpecialChars} special character (!@#$%^&*()_+-=[]{}|;':"\\,./<>?~)`,
    'Must not be a common password',
    'Should not contain personal information',
  ];
};
